import {
  AirPriceRangeTypes, FilterApplier, FilterTypes, FlightFilterApplierValue
} from '@shared/flight-itinerary/entities/filters';
import { stringTimeToSeconds, mapAirlines, SECONDS_IN_HOURS, secondsToStringTime } from '@flight-results/mappers/filters.mapper';
import { SearchPayload } from '@flight-results/entities/request-search.payload';

import { AirportsResponse, RangeType } from '@shared/filters/entities/fetch-filters.response';
import { mapAirFilterTabs } from '@shared/magic-filter-bar/mappers/air-sorter.mapper';
import { AirFilterResponseVm, AirFiltersResponse } from '@shared/magic-filter-bar/entities/filter-types';
import { isNullOrUndefined } from '@app/util/util';
import { SorterTypes } from '@shared/filters/entities/sorter-options';
import { AirResultPackagesPayload } from '@packages/packages-result/entities/air-results';

type SearchByAirFilterPayload = AirResultPackagesPayload | SearchPayload;

export function isCheckBoxByAirFilter(filterType: FilterTypes): boolean {
  return (filterType === FilterTypes.Airlines) ||
    (filterType === FilterTypes.Alliances) ||
    (filterType === FilterTypes.Stops) ||
    (filterType === FilterTypes.BusClass) ||
    (filterType === FilterTypes.AirPriceRanges) ||
    (filterType === FilterTypes.Tags);
}

export function isSectionCheckBoxByAirFilter(filterType: FilterTypes): boolean {
  return (filterType === FilterTypes.ArrivalAirports) || (filterType === FilterTypes.DepartureAirports);
}

export function isCheckRangeByAirFilter(filterType: FilterTypes): boolean {
  return (filterType === FilterTypes.DepartureTimes) ||
    (filterType === FilterTypes.SectorDurations) || (filterType === FilterTypes.PriceRange);
}

export function mapAirFilterPayload(applier: FilterApplier, searchPayload: SearchByAirFilterPayload): SearchByAirFilterPayload {
  let payload = searchPayload;
  if (isCheckBoxByAirFilter(applier.type)) {
    payload = airCheckBoxFilterPayload(applier, searchPayload);
  } else if (isSectionCheckBoxByAirFilter(applier.type)) {
    payload = airAirportsCheckboxPayload(applier, searchPayload);
  } else if (isCheckRangeByAirFilter(applier.type)) {
    payload = airCheckRangeFilterPayload(applier, searchPayload);
  } else if (applier.type === FilterTypes.AirFilterTabs) {
    payload = airFilterTabsPayload(applier, searchPayload);
  }
  return payload;
}

export function mapAirMultipleFilterPayload(
  applier: FilterApplier[], searchPayload: SearchByAirFilterPayload
): SearchByAirFilterPayload {
  let payload = searchPayload;
  applier.forEach(filter => {
    payload = mapAirFilterPayload(filter, payload);
  });
  return payload;
}

export function mappedAirFilterResponseVm(filters: AirFiltersResponse): AirFilterResponseVm {
  const priceRange = filters.MinPrice !== filters.MaxPrice ? {
    PriceRange: {
      Text: FilterTypes.PriceRange,
      From: filters.MinPrice,
      To: filters.MaxPrice,
      RangeType: RangeType.PRICE
    }
  } : {};
  return {
    Airlines: mapAirlines(filters.Airlines),
    AirlinesMinPrice: filters?.Airlines?.map(airline => {
      return {
        ...airline,
        Label: airline.Text
      };
    }),
    BusClass: filters?.BusClass?.map(busClass => {
      return {
        ...busClass,
        Label: busClass.Text
      };
    }),
    ArrivalAirports: filters?.ArrivalAirports?.map(section => mapAiportSection(section)),
    DepartureAirports: filters?.DepartureAirports?.map(section => mapAiportSection(section)),
    DepartureTimes: filters?.DepartureTimes?.filter(time => time.From !== time.To)?.map(time => ({
      ...time,
      SectorNumber: parseInt(time.Text.replace(/\D/g, '')),
      RangeType: RangeType.NUMBER_TIME
    })),
    SectorDurations: filters?.FlightDurations?.filter(duration => duration.From !== duration.To)?.map((duration, index) => ({
      ...duration,
      Text: duration.Text.replace('|', ' > '),
      From: stringTimeToSeconds(duration.From.toString()),
      To: stringTimeToSeconds(duration.To.toString()),
      RangeType: RangeType.TIME,
      SectorNumber: index + 1
    })),
    Alliances: filters?.Alliances?.map(alliance => {
      return {
        ...alliance,
        Label: alliance.Text
      };
    }),
    Stops: filters?.Stops?.map(stop => {
      return {
        ...stop,
        Text: Number(`${stop.Text}`.replace(/^\D+/g, '')),
        Label: `${stop.Text}`.replace(/^\D+/g, '')
      };
    }),
    AirPriceRanges: filters?.AirPriceRanges?.map(filter => {
      const Text = filter.UpperBound && filter.LowerBound ? `${filter.LowerBound} - ${filter.UpperBound}` :
        filter.UpperBound === undefined || filter.UpperBound === null ?
          `${filter.LowerBound} - ${AirPriceRangeTypes.PriceTo}` :
          `${AirPriceRangeTypes.PriceFrom} - ${filter.UpperBound}`;
      return {
        Text, Label: Text,
        Count: filter.Ocurrence
      };
    }),
    AirFilterTabs: mapAirFilterTabs(filters.AirFilterTabs, filters.FlightDurations?.length),
    Tags: filters?.Tags ?? [],
    ...priceRange
  };
}

export function airSearchCheckboxPayload(applier: FilterApplier, payload: SearchByAirFilterPayload): SearchByAirFilterPayload {
  const currentFilters = payload[applier.type];
  const value = applier.value;
  return applier.removable ?
    {
      ...payload,
      [applier.type]: value ? currentFilters?.filter((filter: FlightFilterApplierValue) => filter !== value) : []
    } : {
      ...payload,
      [applier.type]: currentFilters ?
        [...currentFilters?.filter((filter: FlightFilterApplierValue) => filter !== value), value] : []
    };
}

export function airSearchRangeFilter(
  // eslint-disable-next-line @typescript-eslint/typedef
  applier: FilterApplier, payload: SearchByAirFilterPayload, isHourFormat = false): SearchByAirFilterPayload {
  const secondsInHour = isHourFormat ? SECONDS_IN_HOURS : 1;
  return Array.isArray(applier.value) && applier.value.length === 0 ?
    {
      ...payload,
      [applier.type]: payload[applier.type].filter(item => item.SectorNumber !== applier.sector)
    } : {
      ...payload,
      [applier.type]: payload[applier.type].filter(item => item.SectorNumber !== applier.sector).concat([{
        SectorNumber: applier.sector,
        From: secondsToStringTime(parseFloat(applier.value[0]) * secondsInHour, false, false),
        To: secondsToStringTime(parseFloat(applier.value[1]) * secondsInHour, false, false)
      }])
    };
}

export function airPriceRangesCheckboxPayload(
  applier: FilterApplier, payload: SearchByAirFilterPayload
): SearchByAirFilterPayload {
  const arrayRange = applier.value.toString().replace(/\s/g, '').split('-');
  const priceFrom = arrayRange[0] === AirPriceRangeTypes.PriceFrom ? null : parseFloat(arrayRange[0]);
  const priceTo = arrayRange[1] === AirPriceRangeTypes.PriceTo ? null : parseFloat(arrayRange[1]);
  return applier.removable ? {
    ...payload,
    [AirPriceRangeTypes.PriceFrom]: null,
    [AirPriceRangeTypes.PriceTo]: null
  } : {
    ...payload,
    [AirPriceRangeTypes.PriceFrom]: priceFrom,
    [AirPriceRangeTypes.PriceTo]: priceTo
  };
}

function mapAiportSection(section: AirportsResponse): AirportsResponse {
  return {
    ...section,
    Airports: section.Airports.map(airport => {
      return {
        ...airport,
        Label: airport.AirportName
      };
    })
  };
}

function airCheckBoxFilterPayload(applier: FilterApplier, searchPayload: SearchByAirFilterPayload): SearchByAirFilterPayload {
  return applier.type === FilterTypes.AirPriceRanges ?
    airPriceRangesCheckboxPayload(applier, searchPayload) :
    airSearchCheckboxPayload(applier, searchPayload);
}

function airAirportsCheckboxPayload(applier: FilterApplier, payload: SearchByAirFilterPayload): SearchByAirFilterPayload {
  const airports = payload[applier.type].filter(x => x.SectorNumber !== applier.sector);
  const airportToApply = payload[applier.type].find(x => x.SectorNumber === applier.sector);
  const airportFilters = airportToApply ? airportToApply.Airports.filter(x => x !== applier.value.toString()) : [];
  if (applier.removable) {
    if (airportFilters.length > 0) {
      payload = {
        ...payload,
        [applier.type]: [...airports, { SectorNumber: applier.sector, Airports: [...airportFilters] }]
      };
    } else {
      payload = { ...payload, [applier.type]: [...airports] };
    }
  } else {
    payload = {
      ...payload,
      [applier.type]: [
        ...airports, { SectorNumber: applier.sector, Airports: [...airportFilters, applier.value.toString()] }
      ]
    };
  }
  return payload;
}

function airCheckRangeFilterPayload(applier: FilterApplier, searchPayload: SearchByAirFilterPayload): SearchByAirFilterPayload {
  const key = applier.type;
  if (applier.removable) {
    return airCheckRangeFilterRemovablePayload(applier, searchPayload, key);
  } else {
    return airCheckRangeFilterUnremovablePayload(applier, searchPayload, key);
  }
}


function airCheckRangeFilterRemovablePayload(
  applier: FilterApplier, searchPayload: SearchByAirFilterPayload, key: FilterTypes
): SearchByAirFilterPayload {
  let payload = { ...searchPayload };
  if (key === FilterTypes.PriceRange) {
    payload = {
      ...payload,
      [AirPriceRangeTypes.PriceFrom]: undefined,
      [AirPriceRangeTypes.PriceTo]: undefined
    };
  } else {
    const removeRangesPayload = { ...payload };
    removeRangesPayload[key] = applier.sector ? [].concat(removeRangesPayload[key].filter(_filter => {
      return _filter.SectorNumber !== applier.sector;
    })) : [];
    payload = { ...removeRangesPayload };
  }
  return payload;
}

function airCheckRangeFilterUnremovablePayload(
  applier: FilterApplier, searchPayload: SearchByAirFilterPayload, key: FilterTypes
): SearchByAirFilterPayload {
  let payload = { ...searchPayload };
  if (key === FilterTypes.PriceRange) {
    payload = {
      ...payload,
      [AirPriceRangeTypes.PriceFrom]: applier.value[0],
      [AirPriceRangeTypes.PriceTo]: applier.value[1] 
    };
  } else {
    const index = payload[key].findIndex(item => item.SectorNumber === applier.sector);
    if (index !== -1) {
      payload = airSearchRangeFilter(applier, searchPayload, key === FilterTypes.DepartureTimes);
    } else {
      const multiplier = FilterTypes.DepartureTimes !== key ? 1 : SECONDS_IN_HOURS;
      payload = {
        ...payload,
        [key]: [
          ...payload[key], {
            SectorNumber: applier.sector,
            From: secondsToStringTime(parseFloat(applier.value[0]) * multiplier, false, false),
            To: secondsToStringTime(parseFloat(applier.value[1]) * multiplier, false, false)
          }]
      };
    }
  }
  return payload;
}

function airFilterTabsPayload(applier: FilterApplier, searchPayload: SearchByAirFilterPayload): SearchByAirFilterPayload {
  const insertOrderDesc = applier.removable || !isNullOrUndefined(applier?.orderDesc);
  const valueOrderDesc = applier.removable ? false : !!applier?.orderDesc;
  return {
    ...searchPayload,
    OrderCriteria: applier.value as SorterTypes,
    OrderCriteriaSectorNumber: applier.sector,
    ...(insertOrderDesc) && { OrderDesc: valueOrderDesc }
  };
}
